import React, { useState } from 'react';
import { XIcon, HomeIcon, UsersIcon, MapIcon, CalendarIcon, SettingsIcon, NetworkIcon, BriefcaseIcon, SparklesIcon, WifiOffIcon, CalendarDaysIcon, BellIcon } from 'lucide-react';
import { useEvent } from '../../contexts/EventContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAlerts } from '../../contexts/AlertsContext';
interface FloatingMenuProps {
  onNavigate: (page: string) => void;
  currentPage: string;
  isOpen: boolean;
  onClose: () => void;
}
export function FloatingMenu({
  onNavigate,
  currentPage,
  isOpen,
  onClose
}: FloatingMenuProps) {
  const {
    currentEvent,
    allEvents,
    switchEvent,
    isOnline,
    lastSyncTime
  } = useEvent();
  const {
    t,
    isRTL
  } = useLanguage();
  const {
    unreadCount
  } = useAlerts();
  const menuItems = [{
    id: 'home',
    label: t('nav.home'),
    icon: HomeIcon,
    showFor: 'all'
  }, {
    id: 'exhibitors',
    label: t('nav.exhibitors'),
    icon: UsersIcon,
    showFor: 'all'
  }, {
    id: 'floorplan',
    label: t('nav.floorplan'),
    icon: MapIcon,
    showFor: 'all'
  }, {
    id: 'agenda',
    label: t('nav.agenda'),
    icon: CalendarIcon,
    showFor: 'all'
  }, {
    id: 'whatson',
    label: t('nav.whatson'),
    icon: SparklesIcon,
    showFor: 'all'
  }, {
    id: 'favorites',
    label: t('nav.schedule'),
    icon: CalendarDaysIcon,
    showFor: 'all'
  }, {
    id: 'alerts',
    label: t('nav.alerts'),
    icon: BellIcon,
    showFor: 'all'
  }, {
    id: 'networking',
    label: t('nav.networking'),
    icon: NetworkIcon,
    showFor: 'B2B'
  }, {
    id: 'buyers',
    label: t('nav.buyers'),
    icon: BriefcaseIcon,
    showFor: 'B2B'
  }, {
    id: 'settings',
    label: t('nav.settings'),
    icon: SettingsIcon,
    showFor: 'all'
  }];
  const visibleItems = menuItems.filter(item => item.showFor === 'all' || item.showFor === currentEvent.type);
  const getMenuTextColor = () => {
    if (currentEvent.id === 'active') {
      return '#17313a';
    }
    return 'white';
  };
  const formatSyncTime = () => {
    if (!lastSyncTime) return t('menu.syncStatus');
    const now = new Date();
    const syncDate = new Date(lastSyncTime);
    const isToday = now.toDateString() === syncDate.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = yesterday.toDateString() === syncDate.toDateString();
    const timeStr = syncDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    if (isToday) return `Today at ${timeStr}`;
    if (isYesterday) return `Yesterday at ${timeStr}`;
    const dateStr = syncDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    return `${dateStr} at ${timeStr}`;
  };
  const menuTextColor = getMenuTextColor();
  return <>
      {/* Overlay */}
      {isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity" onClick={onClose} data-id="element-3632" />}

      {/* Full Page Menu - RTL aware slide direction */}
      <div className={`fixed inset-0 z-50 transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : isRTL ? '-translate-x-full' : 'translate-x-full'}`} style={{
      backgroundColor: currentEvent.colors.primary,
      color: menuTextColor
    }} data-id="element-3633">
        <div className="h-full overflow-y-auto" data-id="element-3634">
          {/* Header */}
          <div className="p-6 border-b border-opacity-20" style={{
          borderColor: menuTextColor
        }} data-id="element-3635">
            <div className="flex items-center justify-between mb-4" data-id="element-3636">
              <h2 className="text-2xl font-bold font-gotham" data-id="element-3637">
                {t('menu.title')}
              </h2>
              <button onClick={onClose} className="p-2 hover:bg-opacity-10 rounded-lg transition-colors" style={{
              color: menuTextColor,
              backgroundColor: 'transparent'
            }} onMouseEnter={e => e.currentTarget.style.backgroundColor = `${menuTextColor}10`} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'} data-id="element-3638">
                <XIcon className="w-6 h-6" data-id="element-3639" />
              </button>
            </div>
            <p className="opacity-80 text-sm font-gotham" data-id="element-3640">
              {currentEvent.name}
            </p>
          </div>

          {/* Sync Status */}
          <div className="p-6 border-b border-opacity-20" style={{
          borderColor: menuTextColor
        }} data-id="element-3641">
            <div className="flex items-center justify-between" data-id="element-3642">
              <div className="flex items-center gap-3" data-id="element-3643">
                {isOnline ? <SparklesIcon className="w-5 h-5" data-id="element-3644" /> : <WifiOffIcon className="w-5 h-5" data-id="element-3645" />}
                <div data-id="element-3646">
                  <div className="font-semibold font-gotham" data-id="element-3647">
                    {isOnline ? t('menu.connected') : t('menu.offline')}
                  </div>
                  <div className="text-sm opacity-70 font-gotham" data-id="element-3648">
                    {formatSyncTime()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Items */}
          <div className="p-6 border-b border-opacity-20" style={{
          borderColor: menuTextColor
        }} data-id="element-3649">
            <h3 className="text-sm font-semibold opacity-60 mb-3 font-gotham uppercase tracking-wide" data-id="element-3650">
              {t('menu.navigation')}
            </h3>
            <div className="space-y-1" data-id="element-3651">
              {visibleItems.map(item => {
              const Icon = item.icon;
              const isNotifications = item.id === 'alerts';
              const showBadge = isNotifications && unreadCount > 0;
              return <button key={item.id} onClick={() => {
                onNavigate(item.id);
                onClose();
              }} className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors font-gotham relative ${currentPage === item.id ? 'bg-opacity-20' : 'opacity-80'}`} style={{
                backgroundColor: currentPage === item.id ? `${menuTextColor}20` : 'transparent',
                color: menuTextColor
              }} onMouseEnter={e => {
                if (currentPage !== item.id) {
                  e.currentTarget.style.backgroundColor = `${menuTextColor}10`;
                }
              }} onMouseLeave={e => {
                if (currentPage !== item.id) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }} data-id="element-3652">
                    <div className="relative" data-id="element-3653">
                      <Icon className="w-5 h-5" data-id="element-3654" />
                      {showBadge && <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold font-gotham" style={{
                    backgroundColor: '#ef4444'
                  }} data-id="element-3655">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </div>}
                    </div>
                    <span className="font-medium" data-id="element-3656">{item.label}</span>
                  </button>;
            })}
            </div>
          </div>

          {/* Event Switcher */}
          <div className="p-6 border-b border-opacity-20" style={{
          borderColor: menuTextColor
        }} data-id="element-3657">
            <h3 className="text-sm font-semibold opacity-60 mb-3 font-gotham uppercase tracking-wide" data-id="element-3658">
              {t('menu.switchEvent')}
            </h3>
            <div className="space-y-2" data-id="element-3659">
              {allEvents.map(event => <button key={event.id} onClick={() => {
              switchEvent(event.id);
              onClose();
            }} className={`w-full text-${isRTL ? 'right' : 'left'} p-3 rounded-lg transition-colors font-gotham ${event.id === currentEvent.id ? 'bg-opacity-20' : 'opacity-80'}`} style={{
              backgroundColor: event.id === currentEvent.id ? `${menuTextColor}20` : 'transparent',
              color: menuTextColor
            }} onMouseEnter={e => {
              if (event.id !== currentEvent.id) {
                e.currentTarget.style.backgroundColor = `${menuTextColor}10`;
              }
            }} onMouseLeave={e => {
              if (event.id !== currentEvent.id) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }} data-id="element-3660">
                  <div className="font-semibold" data-id="element-3661">{event.name}</div>
                  <div className="text-sm opacity-80" data-id="element-3662">{event.dates}</div>
                </button>)}
            </div>
          </div>

          {/* Sponsor Section */}
          <div className="p-6 border-t border-opacity-20" style={{
          borderColor: menuTextColor
        }} data-id="element-3663">
            <div className="bg-opacity-10 rounded-lg p-4 text-center" style={{
            backgroundColor: menuTextColor
          }} data-id="element-3664">
              <p className="opacity-60 text-xs font-gotham mb-2" data-id="element-3665">
                {t('menu.poweredBy')}
              </p>
              <p className="font-bold text-lg font-gotham" data-id="element-3666">Sponsor Name</p>
            </div>
          </div>
        </div>
      </div>
    </>;
}